import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, VStack, Select, Button, Input, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, useToast, Badge } from "@chakra-ui/react";
import MapBoxField from "../../../components/map/MapBoxField";
import { Navigate, useLocation } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";

export default function AdminProjects() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [districtFilter, setDistrictFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);

  const createDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({ name: "", district: "", address: "", kind: "", contact_name: "", contact_phone: "", contact_email: "" });
  const toast = useToast();

  // Hooks must be unconditional; guard inside effects
  useEffect(() => {
    if (!isSuperUser) return;
    fetchDistricts();
  }, [isSuperUser]);

  useEffect(() => {
    if (!isSuperUser) return;
    const qs = new URLSearchParams(location.search || "");
    const p = parseInt(qs.get("page") || "1", 10);
    setCurrentPage(p);
    fetchProjects(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser, location.search]);

  const fetchDistricts = async () => {
    try {
      const { data } = await client.get("/public/districts/");
      setDistricts(data.results || data);
    } catch (e) {
      // ignore
    }
  };

  const fetchProjects = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (districtFilter) params.set("district", districtFilter);
      const { data } = await client.get(`/projects/?${params.toString()}`);
      const items = data.results || data;
      setProjects(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      toast({ title: "Projeler alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditProject(p);
    setForm({
      name: p.name || "",
      district: urlToId(p.district_info?.url || p.district) || "",
      address: p.address || "",
      kind: p.kind || "",
      contact_name: p.contact_name || "",
      contact_phone: p.contact_phone || "",
      contact_email: p.contact_email || "",
    });
    editDisclosure.onOpen();
  };

  const handleCreate = async () => {
    try {
      await client.post("/projects/", form);
      toast({ title: "Proje oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setForm({ name: "", district: "", address: "", kind: "", contact_name: "", contact_phone: "", contact_email: "" });
      fetchProjects(currentPage);
    } catch (error) {
      toast({ title: "Oluşturma başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!editProject) return;
    try {
      await client.put(editProject.url, form);
      toast({ title: "Proje güncellendi", status: "success" });
      editDisclosure.onClose();
      setEditProject(null);
      fetchProjects(currentPage);
    } catch (error) {
      toast({ title: "Güncelleme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const handleDelete = async (p) => {
    try {
      await client.delete(p.url);
      toast({ title: "Projelerden silindi", status: "success" });
      fetchProjects(currentPage);
    } catch (error) {
      toast({ title: "Silme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>Proje Yönetimi</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <HStack spacing={3}>
          <Select placeholder="İlçe" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} w="240px">
            {districts.map((d) => <option key={d.url || d.name} value={urlToId(d.url)}>{d.name}</option>)}
          </Select>
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchProjects(1); }}>Filtrele</Button>
          <Button colorScheme="green" onClick={createDisclosure.onOpen}>Yeni Proje</Button>
          <ExportButton
            data={(projects || []).map(p => ({
              id: urlToId(p.url),
              ad: p.name || "",
              sehir: p.city_info?.name || '-',
              ilce: p.district_info?.name || '-',
              acik_ticket: p.num_of_open_tickets ?? 0,
              kapali_ticket: p.num_of_closed_tickets ?? 0,
            }))}
            filename="projeler"
            columns={[
              { key: "id", label: "ID" },
              { key: "ad", label: "Ad" },
              { key: "sehir", label: "Şehir" },
              { key: "ilce", label: "İlçe" },
              { key: "acik_ticket", label: "Açık Ticket" },
              { key: "kapali_ticket", label: "Kapalı Ticket" },
            ]}
            sheetName="Projeler"
            excelOptions={{ title: "Projeler" }}
          />
        </HStack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Ad</Th>
              <Th>Şehir</Th>
              <Th>İlçe</Th>
              <Th>Açık Ticket</Th>
              <Th>Kapalı Ticket</Th>
              <Th>Aksiyon</Th>
            </Tr>
          </Thead>
          <Tbody>
            {projects.map((p) => (
              <Tr key={p.url}>
                <Td>{p.name}</Td>
                <Td>{p.city_info?.name || '-'}</Td>
                <Td>{p.district_info?.name || '-'}</Td>
                <Td><Badge colorScheme="orange">{p.num_of_open_tickets ?? 0}</Badge></Td>
                <Td><Badge colorScheme="green">{p.num_of_closed_tickets ?? 0}</Badge></Td>
                <Td>
                  <Button size="xs" variant="outline" onClick={() => openEdit(p)}>Düzenle</Button>
                  <Button size="xs" colorScheme="red" variant="outline" ml={2} onClick={() => handleDelete(p)}>Sil</Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Box mt={4}>
        <Pagination path={"admin/projeler/liste"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Proje Oluştur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İlçe</FormLabel>
                <Select placeholder="İlçe" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}>
                  {districts.map((d) => <option key={d.url || d.name} value={urlToId(d.url)}>{d.name}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Adres</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <Box mt={2}><MapBoxField value={form.address} onChangeAddress={(addr) => setForm({ ...form, address: addr })} /></Box>
              </FormControl>
              <FormControl>
                <FormLabel>Tür</FormLabel>
                <Input value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim Adı</FormLabel>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim Telefon</FormLabel>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim E-posta</FormLabel>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleCreate}>Oluştur</Button>
            <Button onClick={createDisclosure.onClose}>İptal</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Proje Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İlçe</FormLabel>
                <Select placeholder="İlçe" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}>
                  {districts.map((d) => <option key={d.url || d.name} value={urlToId(d.url)}>{d.name}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Adres</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <Box mt={2}><MapBoxField value={form.address} onChangeAddress={(addr) => setForm({ ...form, address: addr })} /></Box>
              </FormControl>
              <FormControl>
                <FormLabel>Tür</FormLabel>
                <Input value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim Adı</FormLabel>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim Telefon</FormLabel>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>İletişim E-posta</FormLabel>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleUpdate}>Güncelle</Button>
            <Button onClick={() => { editDisclosure.onClose(); setEditProject(null); }}>İptal</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
